/**
 * Formats a number with abbreviated suffixes for large values.
 * Values that would round up to `"1000M"` are promoted to the billions tier instead.
 * @param {number | null | undefined} n - The number to format, or nullish for a missing value.
 * @returns {string} A compact string such as `"1.2k"`, `"3.4M"`, `"1.4B"`, or `"—"` if nullish.
 */
const fmtNum = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 999_950_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
};

/**
 * Formats a monetary cost value as a USD string.
 * Values ≥ $1 are shown to two decimal places; smaller values use three to preserve precision.
 * @param {number | string | null | undefined} value - A numeric cost or its string representation, or nullish for a missing value.
 * @returns {string} A formatted string such as `"$1.25"`, `"$0.004"`, or `"—"` if nullish/NaN.
 */
const fmtCost = (value: number | string | null | undefined): string => {
  if (value == null) return '—';
  const n = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (Number.isNaN(n)) return '—';
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(3)}`;
};

/**
 * Formats a duration in milliseconds as a human-readable string.
 * @param {number | null | undefined} ms - The duration in milliseconds, or nullish for a missing value.
 * @returns {string} A string such as `"45s"`, `"3m 12s"`, `"1h 5m"`, or `"—"` if nullish.
 */
const fmtDuration = (ms: number | null | undefined): string => {
  if (ms == null) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

/**
 * Formats a coarse duration in milliseconds, extending {@link fmtDuration} up to
 * days for spans (like time-to-merge) that can run longer than a day.
 * @param {number | null | undefined} ms - The duration in milliseconds, or nullish for a missing value.
 * @returns {string} A string such as `"12m"`, `"5h 20m"`, `"2d 4h"`, or `"—"` if nullish/negative.
 */
const fmtDurationLong = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

/**
 * Parses an ISO 8601 string from the backend, treating naive (zone-less)
 * timestamps as UTC. The BE runs with `USE_TZ=False` and emits strings without
 * a `Z` or offset suffix, which JS's `Date` would otherwise parse as local time.
 *
 * @param {string} iso - An ISO 8601 date string, with or without a zone designator.
 * @returns {Date} A Date parsed in UTC when no zone is present.
 */
const parseBackendIso = (iso: string): Date => {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
};

/**
 * Formats an ISO 8601 timestamp as a relative time string from now.
 * @param {string | null | undefined} iso - An ISO 8601 date string, or nullish for a missing value.
 * @returns {string} A string such as `"just now"`, `"5m ago"`, `"2h ago"`, `"3d ago"`, `"1w ago"`, or `"—"` if nullish/invalid.
 */
const fmtRelative = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const diffMs = Date.now() - parseBackendIso(iso).getTime();
  if (Number.isNaN(diffMs)) return '—';
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
};

/**
 * Formats an ISO 8601 timestamp as a human-readable absolute date/time string in UTC.
 * @param {string | null | undefined} iso - An ISO 8601 date string, or nullish for a missing value.
 * @returns {string} A string such as `"Apr 22, 2026, 14:30 UTC"`, or `"—"` if nullish/invalid.
 */
const fmtAbsolute = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = parseBackendIso(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${datePart}, ${timePart} UTC`;
};

/**
 * Converts a backend turn number to the label shown in the transcript.
 *
 * The backend numbers turns from 0 (see `hook_transformer_service`), while
 * `TurnCard` labels them `Turn {index + 1}`. Every "Turn N" derived from a
 * `turn_number` field must go through this helper so the two never disagree.
 * @param {number} turnNumber - The 0-based `turn_number` from the API.
 * @returns {number} The 1-based number to display.
 */
const turnDisplayNumber = (turnNumber: number): number => turnNumber + 1;

export { fmtAbsolute, fmtCost, fmtDuration, fmtDurationLong, fmtNum, fmtRelative, parseBackendIso, turnDisplayNumber };
